import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";

interface ClassificationAuditEntry {
  timestamp: string;
  user_name: string;
  user_email: string;
  event: string;
  system_type: string;
  system_subtype: string;
  system_confidence: number;
  final_type: string;
  final_subtype: string;
  was_override: boolean;
  type_changed: boolean;
  subtype_changed: boolean;
  input_hash: string | null;
  model: string | null;
}

/**
 * Fetches all classification telemetry events and returns a structured audit summary.
 */
export async function fetchClassificationAuditData(
  workspaceId: string
): Promise<ClassificationAuditEntry[]> {
  const { data, error } = await supabase
    .from("activity_logs")
    .select(`
      id, created_at, activity_type, metadata,
      profiles ( id, name, email )
    `)
    .eq("workspace_id", workspaceId)
    .eq("entity_type", "start_flow")
    .in("activity_type", ["classification_overridden", "classification_confirmed"])
    .order("created_at", { ascending: false })
    .limit(500);

  if (error) throw error;

  return (data || []).map((log: any) => {
    const m = log.metadata || {};
    const delta = m.override_delta || {};
    return {
      timestamp: log.created_at,
      user_name: log.profiles?.name || "-",
      user_email: log.profiles?.email || "-",
      event: log.activity_type,
      system_type: m.system_classified_type || "-",
      system_subtype: m.system_classified_subtype || "-",
      system_confidence: m.system_confidence ?? 0,
      final_type: m.final_type || m.system_classified_type || "-",
      final_subtype: m.final_subtype || m.system_classified_subtype || "-",
      was_override: log.activity_type === "classification_overridden",
      type_changed: delta.type_changed ?? false,
      subtype_changed: delta.subtype_changed ?? false,
      input_hash: m.input_hash || null,
      model: m.model || null,
    };
  });
}

/**
 * Exports classification audit data as a downloadable JSON file.
 */
export async function exportClassificationAuditJSON(workspaceId: string) {
  const entries = await fetchClassificationAuditData(workspaceId);

  const overrides = entries.filter((e) => e.was_override);
  const confirmed = entries.filter((e) => !e.was_override);
  const avgConfidence = entries.length
    ? Math.round(entries.reduce((s, e) => s + e.system_confidence, 0) / entries.length)
    : 0;
  const avgOverrideConfidence = overrides.length
    ? Math.round(overrides.reduce((s, e) => s + e.system_confidence, 0) / overrides.length)
    : 0;

  const exportData = {
    export_version: "1.0",
    exported_at: new Date().toISOString(),
    workspace_id: workspaceId,
    summary: {
      total_classifications: entries.length,
      system_confirmed: confirmed.length,
      user_overrides: overrides.length,
      override_rate_percent: entries.length
        ? Math.round((overrides.length / entries.length) * 100)
        : 0,
      avg_system_confidence: avgConfidence,
      avg_override_confidence: avgOverrideConfidence,
      type_changes: overrides.filter((e) => e.type_changed).length,
      subtype_only_changes: overrides.filter((e) => !e.type_changed && e.subtype_changed).length,
    },
    entries,
  };

  const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `classification-audit-${format(new Date(), "yyyy-MM-dd")}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  return exportData.summary;
}

/**
 * Exports classification audit data as CSV.
 */
export async function exportClassificationAuditCSV(workspaceId: string) {
  const entries = await fetchClassificationAuditData(workspaceId);

  const headers = [
    "Zeitstempel", "Benutzer", "Email", "Event",
    "System-Typ", "System-Subtyp", "System-Konfidenz",
    "Final-Typ", "Final-Subtyp",
    "Override", "Typ geändert", "Subtyp geändert",
    "Input-Hash", "Modell",
  ];

  const rows = entries.map((e) => [
    format(new Date(e.timestamp), "dd.MM.yyyy HH:mm:ss"),
    e.user_name, e.user_email, e.event,
    e.system_type, e.system_subtype, String(e.system_confidence),
    e.final_type, e.final_subtype,
    e.was_override ? "Ja" : "Nein",
    e.type_changed ? "Ja" : "Nein",
    e.subtype_changed ? "Ja" : "Nein",
    e.input_hash || "-", e.model || "-",
  ]);

  const csvContent = [
    headers.join(";"),
    ...rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(";")),
  ].join("\n");

  const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `classification-audit-${format(new Date(), "yyyy-MM-dd")}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  return entries.length;
}
